import React, {
  useContext, useEffect, useState, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Modal from '@material-ui/core/Modal';
import styled from 'styled-components';
import { makeStyles } from '@material-ui/core/styles';
import isEmpty from 'lodash/isEmpty';
import { useDispatch } from 'react-redux';
import { RONGCLOUD_APP_KEY } from 'appenv';
import {
  addMessage, setMessages,
  resetMessages, removeMessage,
} from 'models/messages';
import ChatWindow from './chat/ChatWindow';
import ChatContext from './chat/ChatContext';
import { UserSessionContext } from './UserSession';
import groupChatJoin from '../network/groupChatJoin';

const ModalContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
`;

const useStyles = makeStyles({
  modal: {
    '& [tabindex="-1"]': {
      outline: 0,
    },
  },
});

const RongCloudChat = ({ children }) => {
  const { userSession } = useContext(UserSessionContext);
  const [targetId, setTargetId] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [showChatWindow, setShowChatWindow] = useState(false);
  const [joiningChat, setJoiningChat] = useState(false);
  const [chatRoomTitle, setChatRoomTitle] = useState('Chatroom');
  const dispatch = useDispatch();
  const [hasMoreMsg, setHasMoreMsg] = useState(false);

  const getHistoryMsg = useCallback((timestrap) => {
    const conversationType = type === 'group' ? window.RongIMLib.ConversationType.GROUP : window.RongIMLib.ConversationType.PRIVATE;
    const count = 20;
    window.RongIMClient.getInstance().getHistoryMessages(conversationType, targetId, timestrap, count, {
      onSuccess: (list, hasMsg) => {
        if (list.length > 0) {
          const listJSON = list.map((message) => {
            const messageStr = JSON.stringify(message);
            const messageJSON = JSON.parse(messageStr);
            return messageJSON;
          });
          setHasMoreMsg(hasMsg);
          dispatch(setMessages(listJSON));
        }
      },
      onError: (error) => {
        console.log('Failed to get history messages', error);
      },
    });
  }, [dispatch, type]);

  const connect = () => {
    const { rongcloudToken } = userSession;
    window.RongIMClient.connect(rongcloudToken, {
      onSuccess: () => {
        setStatus('connected');
        setJoiningChat(false);
      },
      onError: () => {},
    });
  };

  const disconnect = () => {
    window.RongIMClient.getInstance().disconnect();
  };

  const setMessageListener = (id) => {
    window.RongIMClient.setOnReceiveMessageListener({
      onReceived: (message) => {
        if (message.targetId === id) {
          const messageStr = JSON.stringify(message);
          const messageJSON = JSON.parse(messageStr);
          switch (message.messageType) {
            case window.RongIMClient.MessageType.TextMessage:
              dispatch(addMessage(messageJSON));
              break;
            case window.RongIMClient.MessageType.ImageMessage:
            case window.RongIMClient.MessageType.HQVoiceMessage:
            case window.RongIMClient.MessageType.RichContentMessage:
            case window.RongIMClient.MessageType.UnknownMessage:
            default:
              break;
          }
        }
      },
    });
  };

  const initialize = () => {
    if (!isEmpty(userSession)) {
      window.RongIMClient.init(RONGCLOUD_APP_KEY);
      window.RongIMLib.RongIMEmoji.init();
      window.RongIMClient.setConnectionStatusListener({
        onChanged: (currentStatus) => {
          switch (currentStatus) {
            case window.RongIMLib.ConnectionStatus.CONNECTED:
              setStatus('connected');
              break;
            case window.RongIMLib.ConnectionStatus.CONNECTING:
              setStatus('connecting');
              break;
            case window.RongIMLib.ConnectionStatus.DISCONNECTED:
              setStatus('disconnected');
              break;
            case window.RongIMLib.ConnectionStatus.KICKED_OFFLINE_BY_OTHER_CLIENT:
            case window.RongIMLib.ConnectionStatus.DOMAIN_INCORRECT:
              setStatus('error');
              break;
            case window.RongIMLib.ConnectionStatus.NETWORK_UNAVAILABLE:
              setStatus('offline');
              connect();
              break;
            default:
              break;
          }
        },
      });
    }
  };

  const sendMessage = (toSendmessage) => {
    if (targetId && toSendmessage) {
      const {
        name,
        customAttendeeFields: {
          vexpo_chat_tag: tag,
          vexpo_chat_user_description: description,
        },
      } = userSession;
      const msg = new window.RongIMLib.TextMessage({
        content: toSendmessage,
        user: {
          name,
          tag,
          description,
        },
      });
      const conversationType = type === 'group' ? window.RongIMLib.ConversationType.GROUP : window.RongIMLib.ConversationType.PRIVATE;

      window.RongIMClient.getInstance().sendMessage(conversationType, targetId, msg, {
        onSuccess: (message) => {
          const messageStr = JSON.stringify(message);
          const messageJSON = JSON.parse(messageStr);
          dispatch(addMessage(messageJSON));
        },
        onError: (errorCode) => {
          const failedMsg = {
            content: {
              content: toSendmessage,
              user: {
                name,
                tag,
                description,
              },
            },
            sentTime: moment().valueOf(),
            sentFailed: errorCode,
            messageId: moment().valueOf(),
            targetId,
            senderUserId: userSession.rongcloudUserId,
          };
          dispatch(addMessage(failedMsg));
        },
      });
    }
  };

  const resendMessage = (message) => {
    dispatch(removeMessage(message.id));
    sendMessage(message.content.content);
  };

  useEffect(() => {
    if (targetId && type && status === 'connected') {
      getHistoryMsg(0);
    }
  }, [status, type, targetId]);

  const leaveChatRoom = () => {
    disconnect();
    dispatch(resetMessages());
    setTargetId(null);
    setType(null);
    setShowChatWindow(false);
    setHasMoreMsg(false);
  };

  async function joinGroupChat(groupId, chatType) {
    initialize();
    setJoiningChat(true);
    setTargetId(groupId);
    setType(chatType);
    setShowChatWindow(true);
    setMessageListener(groupId);
    const { authToken } = userSession;
    const success = await groupChatJoin(authToken, groupId);
    if (success) {
      connect(groupId);
    }
  }

  const classes = useStyles();

  return (
    <ChatContext.Provider
      value={{
        targetId,
        setTargetId,
        type,
        setType,
        joinGroupChat,
        leaveChatRoom,
        setChatRoomTitle,
      }}
    >
      <>
        {children}
        <Modal
          open={showChatWindow}
          aria-labelledby="simple-modal-title"
          aria-describedby="simple-modal-description"
          className={classes.modal}
        >
          <ModalContainer>
            <ChatWindow
              status={status}
              joiningChat={joiningChat}
              chatRoomTitle={chatRoomTitle}
              sendMessage={sendMessage}
              resendMessage={resendMessage}
              getHistoryMsg={getHistoryMsg}
              hasMoreMsg={hasMoreMsg}
            />
          </ModalContainer>
        </Modal>
      </>
    </ChatContext.Provider>
  );
};

RongCloudChat.propTypes = {
  children: PropTypes.node.isRequired,
};

export default RongCloudChat;
